import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { 
  FiUsers, 
  FiShoppingBag, 
  FiDollarSign, 
  FiTrendingUp,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiArrowUp,
  FiArrowDown,
  FiMoreVertical,
  FiEye
} from 'react-icons/fi';
import { BsGraphUp, BsWallet2, BsPeople, BsBoxSeam } from 'react-icons/bs';
import { Helmet } from 'react-helmet-async';
import { 
  fetchDashboardStats, 
  fetchAllTransactions,
  fetchUsers 
} from '../../store/slices/adminSlice';
import { GlassCard } from '../../components/ui/GlassCard';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
} from 'chart.js';
import { format, subDays, startOfDay, endOfDay, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

export const AdminDashboardPage = () => {
  const dispatch = useDispatch();
  const { stats, allTransactions, users, isLoading } = useSelector((state) => state.admin);
  const [timeframe, setTimeframe] = useState('week');
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, [dispatch]);

  const loadDashboardData = async () => {
    await Promise.all([
      dispatch(fetchDashboardStats()),
      dispatch(fetchAllTransactions()),
      dispatch(fetchUsers())
    ]);
  };

  useEffect(() => {
    if (allTransactions && users) {
      // Combine and sort recent activity
      const activities = [
        ...allTransactions.slice(0, 5).map(t => ({
          type: 'transaction',
          data: t,
          date: new Date(t.createdAt)
        })),
        ...users.slice(0, 5).map(u => ({
          type: 'user',
          data: u,
          date: new Date(u.createdAt)
        }))
      ].sort((a, b) => b.date - a.date).slice(0, 10);

      setRecentActivity(activities);
    }
  }, [allTransactions, users]);

  // Generate timeframe dates
  const getTimeframeDates = (frame) => {
    const today = new Date();
    let dates = [];
    let formatStr = 'MMM dd';

    if (frame === 'week') {
      for (let i = 6; i >= 0; i--) {
        dates.push(subDays(today, i));
      }
      formatStr = 'EEE';
    } else if (frame === 'month') {
      for (let i = 29; i >= 0; i--) {
        dates.push(subDays(today, i));
      }
      formatStr = 'MMM dd';
    } else if (frame === 'year') {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      dates = months;
    }

    return { dates, formatStr };
  };

  // Generate revenue data dynamically
  const generateRevenueData = () => {
    const { dates, formatStr } = getTimeframeDates(timeframe);
    const revenue = {};

    // Initialize revenue for each date
    if (timeframe === 'year') {
      dates.forEach((month, idx) => {
        revenue[idx] = 0;
      });
    } else {
      dates.forEach((date) => {
        const key = format(date, 'yyyy-MM-dd');
        revenue[key] = 0;
      });
    }

    // Sum up successful transactions
    if (allTransactions) {
      allTransactions.forEach((transaction) => {
        if (transaction.status === 'success') {
          const transDate = new Date(transaction.createdAt);

          if (timeframe === 'year') {
            const monthIdx = transDate.getMonth();
            revenue[monthIdx] = (revenue[monthIdx] || 0) + (transaction.amount || 0);
          } else {
            const key = format(transDate, 'yyyy-MM-dd');
            if (revenue.hasOwnProperty(key)) {
              revenue[key] += transaction.amount || 0;
            }
          }
        }
      });
    }

    // Format data for chart
    let labels = [];
    let data = [];

    if (timeframe === 'year') {
      labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      data = labels.map((_, idx) => revenue[idx] || 0);
    } else {
      dates.forEach((date) => {
        const key = format(date, 'yyyy-MM-dd');
        labels.push(format(date, formatStr));
        data.push(revenue[key] || 0);
      });
    }

    return {
      labels,
      datasets: [
        {
          label: 'Revenue',
          data,
          borderColor: 'rgb(14, 165, 233)',
          backgroundColor: 'rgba(14, 165, 233, 0.1)',
          tension: 0.4,
          fill: true
        }
      ]
    };
  };

  // Generate platform distribution data dynamically
  const generatePlatformData = () => {
    const platformStats = {};

    if (allTransactions) {
      allTransactions.forEach((transaction) => {
        // Assuming transaction has account info or platform field
        let platform = transaction.account?.platform || transaction.platform || 'Other';
        
        if (!platformStats[platform]) {
          platformStats[platform] = 0;
        }
        platformStats[platform]++;
      });
    }

    // If no platform data, show default distribution
    if (Object.keys(platformStats).length === 0) {
      return {
        labels: ['Facebook', 'Instagram', 'Twitter', 'WhatsApp', 'TikTok'],
        datasets: [
          {
            data: [0, 0, 0, 0, 0],
            backgroundColor: [
              'rgba(59, 130, 246, 0.8)',
              'rgba(168, 85, 247, 0.8)',
              'rgba(37, 99, 235, 0.8)',
              'rgba(34, 197, 94, 0.8)',
              'rgba(236, 72, 153, 0.8)',
            ],
            borderWidth: 0
          }
        ]
      };
    }

    const labels = Object.keys(platformStats);
    const colorMap = {
      'Facebook': 'rgba(59, 130, 246, 0.8)',
      'Instagram': 'rgba(168, 85, 247, 0.8)',
      'Twitter': 'rgba(37, 99, 235, 0.8)',
      'WhatsApp': 'rgba(34, 197, 94, 0.8)',
      'TikTok': 'rgba(236, 72, 153, 0.8)',
    };

    return {
      labels,
      datasets: [
        {
          data: Object.values(platformStats),
          backgroundColor: labels.map(label => colorMap[label] || 'rgba(107, 114, 128, 0.8)'),
          borderWidth: 0
        }
      ]
    };
  };

  const revenueData = generateRevenueData();
  const platformData = generatePlatformData();

  // Calculate sales conversion rate
  const calculateConversionRate = () => {
    if (!stats?.totalAccounts || stats.totalAccounts === 0) return 0;
    return Math.round((stats.soldAccounts / stats.totalAccounts) * 100);
  };

  const conversionRate = calculateConversionRate();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#6B7280',
          font: {
            size: 12
          }
        }
      }
    },
    cutout: '70%'
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin Dashboard | SocialMarket</title>
      </Helmet>

      <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-luxury-50 via-white to-luxury-100">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="mb-8"
          >
            <h1 className="text-3xl font-display font-bold text-luxury-900 mb-2">
              Admin <span className="gradient-text">Dashboard</span>
            </h1>
            <p className="text-luxury-600">
              Welcome back, Administrator. Here's what's happening with your platform.
            </p>
          </motion.div>

          {/* Stats Grid */}
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          >
            <motion.div variants={fadeInUp}>
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <FiUsers className="w-6 h-6 text-blue-600" />
                  </div>
                  <span className="text-sm text-luxury-500">Total Users</span>
                </div>
                <h3 className="text-2xl font-bold text-luxury-900 mb-1">
                  {stats?.totalUsers?.toLocaleString() || '0'}
                </h3>
                <p className="text-sm text-green-600 flex items-center">
                  <FiArrowUp className="w-3 h-3 mr-1" />
                  +12% from last month
                </p>
              </GlassCard>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <FiShoppingBag className="w-6 h-6 text-green-600" />
                  </div>
                  <span className="text-sm text-luxury-500">Total Accounts</span>
                </div>
                <h3 className="text-2xl font-bold text-luxury-900 mb-1">
                  {stats?.totalAccounts?.toLocaleString() || '0'}
                </h3>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-luxury-500">
                    Available: {stats?.availableAccounts || 0}
                  </span>
                  <span className="text-luxury-500">
                    Sold: {stats?.soldAccounts || 0}
                  </span>
                </div>
              </GlassCard>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <FiDollarSign className="w-6 h-6 text-purple-600" />
                  </div>
                  <span className="text-sm text-luxury-500">Total Revenue</span>
                </div>
                <h3 className="text-2xl font-bold text-luxury-900 mb-1">
                  ₦{stats?.totalRevenue?.toLocaleString() || '0'}
                </h3>
                <p className="text-sm text-green-600 flex items-center">
                  <FiArrowUp className="w-3 h-3 mr-1" />
                  +8.2% from last month
                </p>
              </GlassCard>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <FiTrendingUp className="w-6 h-6 text-orange-600" />
                  </div>
                  <span className="text-sm text-luxury-500">Sales Conversion</span>
                </div>
                <h3 className="text-2xl font-bold text-luxury-900 mb-1">{conversionRate}%</h3>
                <p className="text-sm text-luxury-600">
                  {stats?.soldAccounts || 0} of {stats?.totalAccounts || 0} accounts sold
                </p>
              </GlassCard>
            </motion.div>
          </motion.div>

          {/* Charts Row */}
          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            {/* Revenue Chart */}
            <motion.div
              variants={fadeInUp}
              initial="initial"
              animate="animate"
              className="lg:col-span-2"
            >
              <GlassCard className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-luxury-900">Revenue Overview</h2>
                  <div className="flex space-x-2">
                    {['week', 'month', 'year'].map((t) => (
                      <button
                        key={t}
                        onClick={() => setTimeframe(t)}
                        className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                          timeframe === t
                            ? 'bg-primary-600 text-white'
                            : 'glass hover:bg-primary-50'
                        }`}
                      >
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="h-80">
                  <Line data={revenueData} options={chartOptions} />
                </div>
              </GlassCard>
            </motion.div>

            {/* Platform Distribution */}
            <motion.div variants={fadeInUp} initial="initial" animate="animate">
              <GlassCard className="p-6 h-full">
                <h2 className="text-lg font-semibold text-luxury-900 mb-6">
                  Platform Distribution
                </h2>
                <div className="h-64">
                  <Doughnut data={platformData} options={doughnutOptions} />
                </div>
                <div className="mt-6 space-y-2">
                  {platformData.labels.map((label, index) => (
                    <div key={label} className="flex items-center justify-between text-sm">
                      <div className="flex items-center">
                        <div 
                          className="w-3 h-3 rounded-full mr-2"
                          style={{ backgroundColor: platformData.datasets[0].backgroundColor[index] }}
                        />
                        <span className="text-luxury-600">{label}</span>
                      </div>
                      <span className="font-medium text-luxury-900">
                        {platformData.datasets[0].data[index]}%
                      </span>
                    </div>
                  ))}
                </div>
              </GlassCard>
            </motion.div>
          </div>

          {/* Recent Activity & Quick Actions */}
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Recent Activity */}
            <motion.div
              variants={fadeInUp}
              initial="initial"
              animate="animate"
              className="lg:col-span-2"
            >
              <GlassCard className="p-6">
                <h2 className="text-lg font-semibold text-luxury-900 mb-6">
                  Recent Activity
                </h2>
                <div className="space-y-4">
                  {recentActivity.map((activity, index) => {
                    if(activity.data.status ==="pending") return null;
                    return (

                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          activity.type === 'user' ? 'bg-blue-100' : 'bg-green-100'
                        }`}>
                          {activity.type === 'user' ? (
                            <FiUsers className={`w-5 h-5 ${
                              activity.type === 'user' ? 'text-blue-600' : 'text-green-600'
                            }`} />
                          ) : (
                            <FiDollarSign className="w-5 h-5 text-green-600" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-luxury-900">
                            {activity.type === 'user' 
                              ? `New user registered: ${activity.data.username}`
                              : `New ${activity.data.type}: ₦${activity.data.amount?.toLocaleString()}`
                            }
                          </p>
                          <p className="text-xs text-luxury-500">
                            {format(activity.date, 'MMM dd, yyyy • h:mm a')}
                          </p>
                        </div>
                      </div>
                      <Link 
                        to={activity.type === 'user' ? '/admin/users' : '/admin/transactions'}
                        className="text-primary-600 hover:text-primary-700"
                      >
                        <FiEye className="w-4 h-4" />
                      </Link>
                    </div>
                    )
                  })}
                </div>
              </GlassCard>
            </motion.div>

            {/* Quick Actions */}
            <motion.div variants={fadeInUp} initial="initial" animate="animate">
              <GlassCard className="p-6">
                <h2 className="text-lg font-semibold text-luxury-900 mb-6">
                  Quick Actions
                </h2>
                <div className="space-y-3">
                  <Link to="/admin/accounts/new">
                    <button className="w-full p-4 glass rounded-xl hover:bg-primary-50 transition-colors text-left">
                      <p className="font-medium text-luxury-900">Add New Account</p>
                      <p className="text-sm text-luxury-500">Create a new social media listing</p>
                    </button>
                  </Link>
                  <Link to="/admin/users">
                    <button className="w-full p-4 glass rounded-xl hover:bg-primary-50 transition-colors text-left">
                      <p className="font-medium text-luxury-900">Manage Users</p>
                      <p className="text-sm text-luxury-500">View and manage user accounts</p>
                    </button>
                  </Link>
                  <Link to="/admin/transactions">
                    <button className="w-full p-4 glass rounded-xl hover:bg-primary-50 transition-colors text-left">
                      <p className="font-medium text-luxury-900">View Transactions</p>
                      <p className="text-sm text-luxury-500">Monitor all platform transactions</p>
                    </button>
                  </Link>
                  <Link to="/admin/payment-config">
                    <button className="w-full p-4 glass rounded-xl hover:bg-primary-50 transition-colors text-left">
                      <p className="font-medium text-luxury-900">Payment Configuration</p>
                      <p className="text-sm text-luxury-500">Manage payment methods & API keys</p>
                    </button>
                  </Link>
                  <Link to="/admin/analytics">
                    <button className="w-full p-4 glass rounded-xl hover:bg-primary-50 transition-colors text-left">
                      <p className="font-medium text-luxury-900">Analytics Dashboard</p>
                      <p className="text-sm text-luxury-500">View detailed statistics & reports</p>
                    </button>
                  </Link>
                  <button className="w-full p-4 glass rounded-xl hover:bg-primary-50 transition-colors text-left">
                    <p className="font-medium text-luxury-900">Generate Report</p>
                    <p className="text-sm text-luxury-500">Export platform analytics</p>
                  </button>
                </div>
              </GlassCard>
            </motion.div>
          </div>

          {/* Recent Transactions Table */}
          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="mt-8"
          >
            <GlassCard className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-luxury-900">
                  Recent Transactions
                </h2>
                <Link 
                  to="/admin/transactions"
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  View All
                </Link>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-luxury-500">
                      <th className="pb-4">User</th>
                      <th className="pb-4">Type</th>
                      <th className="pb-4">Amount</th>
                      <th className="pb-4">Status</th>
                      <th className="pb-4">Date</th>
                      <th className="pb-4">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allTransactions?.slice(0, 5).map((transaction) => (
                      <tr key={transaction._id} className="border-t border-luxury-200">
                        <td className="py-3">
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-luxury-200 rounded-full flex items-center justify-center">
                              <FiUsers className="w-4 h-4 text-luxury-500" />
                            </div>
                            <span className="text-sm font-medium text-luxury-900">
                              {transaction.user?.username}
                            </span>
                          </div>
                        </td>
                        <td className="py-3">
                          <span className="text-sm capitalize text-luxury-600">
                            {transaction.type}
                          </span>
                        </td>
                        <td className="py-3">
                          <span className="text-sm font-medium text-luxury-900">
                            ₦{transaction.amount?.toLocaleString()}
                          </span>
                        </td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            transaction.status === 'success' ? 'bg-green-100 text-green-700' :
                            transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {transaction.status}
                          </span>
                        </td>
                        <td className="py-3">
                          <span className="text-sm text-luxury-500">
                            {format(new Date(transaction.createdAt), 'MMM dd, yyyy')}
                          </span>
                        </td>
                        <td className="py-3">
                          <Link to={`/admin/transactions?id=${transaction._id}`}>
                            <button className="text-primary-600 hover:text-primary-700">
                              <FiEye className="w-4 h-4" />
                            </button>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </GlassCard>
          </motion.div>
        </div>
      </div>
    </>
  );
};